import { SnackbarContent } from "components/global/snackbar/SnackbarContent";
import { useAppSelector } from "hooks/utilsHooks/storeHooks";
import {
	createContext,
	type FC,
	type PropsWithChildren,
	type ReactElement,
	useContext,
	useEffect,
	useState,
} from "react";
import { useTranslation } from "react-i18next";
import { selectAppState } from "slices/appStateSlice";
import { TransactionMode, TransactionState } from "types";
import { AppState } from "types/mainTypes";
import { useOrderSteps } from "../hooks/useOrderSteps";
import { selectCurrentOrderStep, selectOrder, selectOrderDishes, selectRefillMode } from "../slices/transactionSlice";
import { useModalContext } from "./ModalContext";
import { useSnackbarContext } from "./SnackbarContext";

type DrawerContextProps = {
	open: boolean;
	position?: "left" | "right";
	toggleDrawer: (open: boolean) => void;
	currentOrderStepScreen: ReactElement;
	currentOrderStep: number;
};

export const DrawerContext = createContext<DrawerContextProps>({
	open: false,
	position: "left",
	toggleDrawer: () => null,
	currentOrderStepScreen: <></>,
	currentOrderStep: 1,
});

export const DrawerContextProvider: FC<PropsWithChildren> = ({ children }) => {
	const { t } = useTranslation();
	const { setModal } = useModalContext();
	const { modal } = useModalContext();
	const order = useAppSelector(selectOrder);
	const orderDishes = useAppSelector(selectOrderDishes);
	const currentOrderStep = useAppSelector(selectCurrentOrderStep);
	const [isOpen, setIsOpen] = useState(order != null && order.state !== TransactionState.Finished);
	const currentOrderStepScreen = useOrderSteps({
		open: isOpen,
		toggleDrawer: setIsOpen,
	});
	const { openSnackbarWithContent } = useSnackbarContext();
	const appState = useAppSelector(selectAppState);
	const refillMode = useAppSelector(selectRefillMode);
	const isRemoteRefill = refillMode === TransactionMode.Remote;
	const handleSetOrderStep1 = () => {
		setIsOpen(true);
		setModal(null);
	};
	const handleSetOrderStep2 = () => {
		const userAllergens = order?.user?.preferences?.allergens ?? false;
		const userHasPreselectedAllergens = userAllergens && userAllergens?.length > 0;
		setIsOpen(true);
		setModal(null);
		if (!isRemoteRefill) {
			openSnackbarWithContent(
				<SnackbarContent
					variant="welcome"
					user={order?.user}
					subheader={userHasPreselectedAllergens ? t("order.step2.snackbar.subheaderAllergens") : undefined}
				/>,
			);
		}
	};

	const handleSetOrderStep3 = () => {
		setIsOpen(true);
		setModal(null);
	};

	const handleSetOrderStep5 = () => {
		setModal(null);
		if (!isOpen) {
			setIsOpen(true);
		}
		if (!isRemoteRefill) {
			if (orderDishes.length === 0) {
				openSnackbarWithContent(<SnackbarContent variant="emptyOrder" user={order?.user} />);
			} else {
				openSnackbarWithContent(<SnackbarContent variant="orderFinished" user={order?.user} />);
			}
		}
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: intentionally reacts only to appState transitions — step changes are handled by the effect below
	useEffect(() => {
		const shouldResetOrder1State =
			appState === AppState.IDLE &&
			(!order || order?.state === TransactionState.Finished) &&
			currentOrderStep === 1 &&
			!isOpen;

		if (shouldResetOrder1State) {
			setIsOpen(true);
		}
	}, [appState]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: handlers read current state via closure; adding them as deps causes re-runs every render since they're not memoized
	useEffect(() => {
		switch (currentOrderStep) {
			case 1:
				handleSetOrderStep1();
				break;
			case 2:
				handleSetOrderStep2();
				break;
			case 3:
				handleSetOrderStep3();
				break;
			case 4:
				setModal(null);
				if (!isOpen) {
					setIsOpen(true);
				}
				break;
			case 5:
				handleSetOrderStep5();
				break;
			default:
				setModal(null);
				if (!isOpen) {
					setIsOpen(true);
				}
		}
	}, [currentOrderStep]);

	useEffect(() => {
		if (modal) {
			setIsOpen(false);
		}
	}, [modal]);

	return (
		<DrawerContext.Provider
			value={{
				open: isOpen,
				toggleDrawer: setIsOpen,
				currentOrderStep: currentOrderStep,
				currentOrderStepScreen: currentOrderStepScreen,
			}}
		>
			{children}
		</DrawerContext.Provider>
	);
};

export const useDrawerContext = () => useContext(DrawerContext);
